import { CheckIcon, DurationIcon, UserChangedIcon } from "@xola/icons";
import { produce } from "immer";
import { WritableDraft } from "immer/dist/types/types-external";
import { isArray } from "lodash";
import React, { ReactNode } from "react";
import { Audit, GuestCheckInAction, GuestStatus, Purchase, PurchaseItem } from "types";
import { GuestStatusChangedItemProps } from "@/components/PurchasesMenu/Timeline/Items/GuestStatusChangedItem";
import { TimelineItem } from "@/components/PurchasesMenu/Timeline/TimelineItem";

type GuestCheckInItemProps = GuestStatusChangedItemProps;

export const GuestCheckInItem = ({ purchase, item, audit, showDate }: GuestCheckInItemProps) => {
    const match = GuestCheckInItem.supportsAudit(purchase, item, audit);

    if (!match) {
        return null;
    }

    const matchedGuest: any = Object.values(match.guests)[0];
    if (!matchedGuest) {
        return null;
    }

    let allChanged = false;
    if (match.guests.length === item.guests.length) {
        /** Check if new `checkInCount` is same for all the guests in the audit */
        allChanged = match.guests.every((guest: any) => guest.checkInCount?.new === matchedGuest.checkInCount?.new);
    }

    if (allChanged) {
        // All guests are in the same status because guest level check-in is not enabled
        const [from, to] = [matchedGuest.checkInCount?.old, matchedGuest.checkInCount?.new];
        return <AllGuestsCheckIn from={from} to={to} audit={audit} showDate={showDate} />;
    }

    /** One or multiple guests are checked in during this audit, so we need to show check-in timeline item for all the checked in guests */
    const checkedInGuests: any[] = match.guests.filter((guest: any) => !!guest.checkInCount) ?? [];

    return (
        <>
            {checkedInGuests.map((auditGuest, index) => {
                const [from, to] = [auditGuest.checkInCount?.old, auditGuest.checkInCount?.new];
                const guest = item.guests.find((guest) => guest.id === auditGuest.id);
                const isFirstGuest = index === 0;

                return (
                    <SingleGuestCheckIn
                        key={auditGuest.id}
                        from={from}
                        to={to}
                        audit={audit}
                        showDate={isFirstGuest ? showDate : false}
                        matchedGuest={auditGuest}
                        guest={guest}
                    />
                );
            })}
        </>
    );
};

/**
 * IMPORTANT: For audits after multi check-in functionality (XS-126) would have `checkInCount` data,
 * but for migrated purchases and older check-ins, `checkInCount` would not be there.
 * For these audits, we have to rely on `guestStatus: pending->arrived` for check-in and vice versa
 **/
GuestCheckInItem.supportsAudit = (_purchase: Purchase, item: PurchaseItem, audit: Audit) => {
    return getCheckInCountItem(item, audit);
};

const getCheckInCountItem = (item: PurchaseItem, audit: Audit) => {
    if (!audit.data?.items || !isArray(audit.data?.items)) {
        return null;
    }

    /** For migrated purchase and check-ins before (XS-126), we'll add `checkInCount` using guestStatus */
    const auditItemsWithCheckInCount = audit.data.items.map((auditItem: any) => {
        if (auditItem?.id !== item?.id) {
            return auditItem;
        }

        return produce(auditItem, (draft: WritableDraft<any>) => {
            draft.guests = Object.values(draft.guests || {}) ?? [];
            const hasCheckInCount = draft.guests.some((guest: any) => !!guest.checkInCount);

            if (!hasCheckInCount) {
                for (const guest of draft.guests) {
                    if (!guest.guestStatus?.old || !guest.guestStatus?.new) {
                        continue;
                    }

                    const { old: oldStatus, new: newStatus } = guest.guestStatus;

                    if (oldStatus === GuestStatus.PENDING && newStatus === GuestStatus.ARRIVED) {
                        guest.checkInCount = { old: 0, new: 1 };
                    } else if (
                        oldStatus === GuestStatus.ARRIVED &&
                        (newStatus === GuestStatus.PENDING || newStatus === GuestStatus.NO_SHOW)
                    ) {
                        guest.checkInCount = { old: 1, new: 0 };
                    }
                }
            }
        });
    });

    return auditItemsWithCheckInCount.find((auditItem: any) => {
        if (!auditItem.guests || auditItem?.id !== item?.id) {
            return false;
        }

        const guests: any = Object.values(auditItem.guests) ?? [];
        return guests.some((guest: any) => !!guest.checkInCount);
    });
};

interface GuestCheckInTimelineItemProps {
    action: GuestCheckInAction;
    audit: Audit;
    children: ReactNode;
    showDate?: boolean;
}

const GuestCheckInTimelineItem = ({ audit, children, action, showDate }: GuestCheckInTimelineItemProps) => {
    return (
        <TimelineItem
            title={dataMap[action].title}
            audit={audit}
            icon={dataMap[action].icon ?? <CheckIcon />}
            color={dataMap[action].color ?? "primary"}
            showDate={showDate}
        >
            {children}
        </TimelineItem>
    );
};

interface CheckInSubItemProps {
    from: number;
    to: number;
    audit: Audit;
    showDate?: boolean;
}

const AllGuestsCheckIn = ({ from, to, ...rest }: CheckInSubItemProps) => {
    const isUndoCheckIn = to < from;

    return (
        <GuestCheckInTimelineItem
            action={isUndoCheckIn ? GuestCheckInAction.UNDO_CHECK_IN : GuestCheckInAction.CHECK_IN}
            {...rest}
        >
            <div className="space-x-2">
                <span>{isUndoCheckIn ? "All guests undid the check-in" : `All guests were checked-in`}</span>
            </div>
        </GuestCheckInTimelineItem>
    );
};

interface SingleGuestCheckInProps extends CheckInSubItemProps {
    guest: any;
    matchedGuest: any;
}

const SingleGuestCheckIn = ({ from, to, guest, matchedGuest, ...rest }: SingleGuestCheckInProps) => {
    const isUndoCheckIn = to < from;

    return (
        <GuestCheckInTimelineItem
            action={isUndoCheckIn ? GuestCheckInAction.UNDO_CHECK_IN : GuestCheckInAction.CHECK_IN}
            {...rest}
        >
            <div className="space-x-2">
                {isUndoCheckIn ? (
                    <span>
                        <var data-var="guestName">{guest?.name ?? `Guest ${matchedGuest.id}`}</var> undid the check-in
                    </span>
                ) : (
                    <span>
                        <var data-var="guestName">{guest?.name ?? `Guest ${matchedGuest.id}`}</var> checked in
                    </span>
                )}
            </div>
        </GuestCheckInTimelineItem>
    );
};

const dataMap: Record<GuestCheckInAction, any> = {
    [GuestCheckInAction.CHECK_IN]: {
        title: "Check-In",
        color: "success",
        icon: <CheckIcon />,
        badge: <CheckIcon />,
    },
    [GuestCheckInAction.UNDO_CHECK_IN]: {
        title: "Undo Check-In",
        color: "warning",
        icon: <UserChangedIcon />,
        badge: <DurationIcon />,
    },
};
